import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import { API_URL } from '../config';

export default function Activities({ companies, users, currentUserId, userRole }) {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState('7'); // Filtr podle dnů
  const [selectedUser, setSelectedUser] = useState(''); // Filtr podle uživatele (pro admina/teamleadera)
  const [visibleUsers, setVisibleUsers] = useState([]); // uživatelé, na které smí actor přepnout
  const [canSwitchUsers, setCanSwitchUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState(''); // Vyhledávání podle názvu firmy, IČO nebo obsahu aktivity

  // Načti aktivity z backendu (se scope dle oprávnění → admin/teamleader vidí i ostatní uživatele)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUserId) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ userId: currentUserId, days: selectedDays });
        if (selectedUser) params.set('viewUserId', selectedUser);
        const res = await fetch(`${API_URL}/activities?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setActivities(Array.isArray(data.activities) ? data.activities : []);
        setVisibleUsers(Array.isArray(data.visibleUsers) ? data.visibleUsers : []);
        // Přepínání uživatelů umožni adminovi a komukoli, kdo vidí víc než jen sebe (teamleader)
        setCanSwitchUsers(!!data.isAdmin || (Array.isArray(data.visibleUsers) && data.visibleUsers.length > 1));
      } catch (err) {
        console.error('Chyba při načítání aktivit:', err);
        if (!cancelled) setActivities([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentUserId, selectedDays, selectedUser]);

  useEffect(() => {
    filterActivities();
  }, [activities, searchQuery]);

  const filterActivities = () => {
    let filtered = [...activities];

    // Filtr podle vyhledávacího dotazu (ostatní filtry řeší backend)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(activity => 
        activity.companyName?.toLowerCase().includes(query) ||
        activity.companyIco?.toLowerCase().includes(query) ||
        activity.text?.toLowerCase().includes(query) ||
        activity.status?.toLowerCase().includes(query)
      );
    }

    setFilteredActivities(filtered);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isToday = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-800 dark:to-gray-900 py-10">
      <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
        <PageHeader
          title="📋 Všechny aktivity"
        />
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            {/* Filtry */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Vyhledávací pole */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vyhledat</label>
                <input
                  type="text"
                  placeholder="Název firmy, IČO nebo obsah aktivity..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-64"
                />
              </div>
              
              {/* Filtr podle dnů */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zobrazit aktivity za</label>
                <select
                  value={selectedDays}
                  onChange={(e) => setSelectedDays(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                >
                  <option value="1">Poslední den</option>
                  <option value="3">Poslední 3 dny</option>
                  <option value="7">Poslední týden</option>
                  <option value="14">Poslední 2 týdny</option>
                  <option value="30">Poslední měsíc</option>
                  <option value="all">Všechny aktivity</option>
                </select>
              </div>
              
              {/* Filtr podle uživatele (admin a teamleader) */}
              {canSwitchUsers && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Uživatel</label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                  >
                    <option value="">Všichni uživatelé</option>
                    {(visibleUsers.length ? visibleUsers : (users || [])).map(user => (
                      <option key={user._id} value={user._id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-8 flex flex-col items-center shadow-2xl">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
                <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">Načítám aktivity...</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Zpracovávám všechny aktivity firem</p>
              </div>
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">
                <div className="text-4xl mb-2">📝</div>
                <div>Žádné aktivity odpovídající kritériím</div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredActivities.map((activity, index) => {
                const isOverdueActivity = isOverdue(activity.nextActionDate);
                const isTodayActivity = isToday(activity.nextActionDate);
                
                let cardClass = "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow";
                if (isOverdueActivity) cardClass += " border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/20";
                else if (isTodayActivity) cardClass += " border-l-4 border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20";
                else cardClass += " border-l-4 border-l-blue-500";

                return (
                  <div 
                    key={activity._id || index} 
                    className={cardClass}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                      {/* Informace o firmě */}
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                          {activity.companyName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          IČO: {activity.companyIco || '-'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Vlastník: {activity.companyOwnerName}
                        </div>
                      </div>

                      {/* Popis aktivity */}
                      <div className="lg:col-span-2">
                        <div className="text-sm text-gray-900 dark:text-white mb-2 leading-tight" style={{whiteSpace: 'pre-wrap'}}>
                          {activity.text}
                        </div>
                        {activity.status && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {activity.status}
                          </span>
                        )}
                      </div>

                      {/* Datum a čas */}
                      <div className="text-right">
                        <div className="flex flex-col gap-1">
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            📅 Zapsáno: {formatDateTime(activity.createdAt)}
                          </div>
                          {(activity.createdBy || activity.createdBy === null) && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              👤 Uložil: {
                                activity.createdBy 
                                  ? (typeof activity.createdBy === 'object' 
                                      ? activity.createdBy.name 
                                      : users?.find(u => u._id === activity.createdBy)?.name || 'Systém')
                                  : 'Systém'
                              }
                            </div>
                          )}
                          {activity.nextActionDate && (
                            <div className={`text-xs font-medium ${
                              isOverdueActivity ? 'text-red-600 dark:text-red-400' :
                              isTodayActivity ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-gray-500 dark:text-gray-400'
                            }`}>
                              {isOverdueActivity && '🔥 '}
                              {isTodayActivity && '📅 '}
                              ⏰ Další akce: {formatDateTime(activity.nextActionDate)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Tlačítko Detail */}
                      <div className="flex justify-center lg:justify-end items-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/companies/${activity.companyId}`);
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md shadow-sm transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          Detail
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
